import type {
  UserApi,
  UserApiUpdatePasswordQuery,
  UserApiUpdateQuery,
  UserNextStepApi,
  UserProgressApi,
} from "@/Modules/Models/Api/User";
import type { UserEndTrialApiQuery } from "@/Modules/Models/Api/User";
import type { UserPromoCodeApiQuery } from "@/Modules/Models/Api/User";
import type { UserB2bDocsApi } from "@/Modules/Models/Api/User";

import { get2, patch2, post2, remove2 } from "./utils/FunctionalTherapyApi";

export function getMe(): Promise<UserApi> {
  return get2<void, UserApi>("/v2.0/users/me");
}

export function getMeProgress(): Promise<UserProgressApi> {
  return get2<void, UserProgressApi>("/v1.0/users/me/progress");
}

export function getById(uuid: string): Promise<UserApi> {
  return get2<void, UserApi>(`/v2.0/users/${uuid}`);
}

export function getUserByEmail(email: string): Promise<UserApi[]> {
  return get2<{ email: string }, UserApi[]>(`/v1.0/users?email=${email}`, undefined, {
    auth: "appToken",
  });
}

export function updateUser(userApiUpdateQuery: UserApiUpdateQuery): Promise<UserApi> {
  return patch2<UserApiUpdateQuery, UserApi>(`/v2.0/users/me`, userApiUpdateQuery);
}

export function updateUserPassword(
  userId: string,
  userApiUpdatePasswordQuery: UserApiUpdatePasswordQuery
): Promise<void> {
  return patch2<UserApiUpdatePasswordQuery, void>(
    `/v1.0/users/${userId}/changePassword`,
    userApiUpdatePasswordQuery
  );
}

export function sendMissingEmail(userId: string) {
  return post2<void, boolean>(`/v1.0/users/${userId}/missing-user`);
}

export function discharge(userId: string, reason: string): Promise<unknown> {
  return post2<{ discharge_reason: string }, unknown>(`/v1.0/users/${userId}/discharge`, {
    discharge_reason: reason,
  });
}

export function endTrialPeriod(userId: string): Promise<UserApi> {
  return post2<UserEndTrialApiQuery, UserApi>(`/v1.0/users/end-trial`, { user_uuid: userId });
}

export function setPromoCode(userId: string, code: string): Promise<UserApi> {
  return post2<UserPromoCodeApiQuery, UserApi>(`v1.0/users/${userId}/codes`, { code });
}

export function deleteAccount(userId: string): Promise<void> {
  return remove2<void, void>(`/v1.0/users/${userId}`);
}

export function showTherapist(): Promise<void> {
  return post2<void, void>("/v1.0/users/therapist_show");
}

export function getLastTherapist(): Promise<UserApi> {
  return get2<void, UserApi>("/v1.0/users/last_therapist");
}

export function getUserB2bDocs(): Promise<UserB2bDocsApi[]> {
  return get2<void, UserB2bDocsApi[]>("/v1.0/users/b2bdocs");
}

export function setUserB2bDocs(docs: string[]): Promise<void> {
  return post2<string[], void>("/v1.0/users/b2bdocs", docs);
}

export function getNextSteps(): Promise<UserNextStepApi[]> {
  return get2<void, UserNextStepApi[]>("/v1.0/users/me/steps");
}
